import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatError, DIFFICULTIES } from "@/lib/api";

const NUMS = [["ai_requests", "AI requests / period"], ["explanations", "AI explanations / period (blank = within AI requests)"], ["quizzes", "Quizzes / period"], ["cost_cap_usd", "AI cost hard cap / period (USD)"]];
const FLAGS = [["flashcards", "AI flashcards"], ["interview", "Interview practice"], ["interview_feedback", "AI interview feedback"], ["mock_tests", "Timed mock interviews"], ["timed_quiz", "Timed quizzes"]];
const PRICES = [["price_monthly", "Monthly (₹)"], ["price_yearly", "Yearly (₹)"], ["price_monthly_usd", "Monthly ($)"], ["price_yearly_usd", "Yearly ($)"]];

const Chip = ({ on, onClick, children, testId }) => (
  <button type="button" data-testid={testId} onClick={onClick}
    className={`rounded-full border px-3 py-1 text-xs font-medium capitalize transition-colors ${on ? "border-brand bg-solid text-white" : "border-line text-ink-2 hover:border-ink-3"}`}>{children}</button>
);

function LimitsFields({ id, l, setL, subjects }) {
  const toggle = (k, v) => setL({ ...l, [k]: l[k].includes(v) ? l[k].filter((x) => x !== v) : [...l[k], v] });
  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
        <div className="flex flex-col gap-1.5"><Label className="text-xs">Quota period</Label>
          <Select value={l.period} onValueChange={(v) => setL({ ...l, period: v })}>
            <SelectTrigger data-testid={`plan-${id}-period`}><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="day">Per day</SelectItem><SelectItem value="month">Per 30 days</SelectItem></SelectContent>
          </Select></div>
        {NUMS.map(([k, label]) => (
          <div key={k} className="flex flex-col gap-1.5"><Label className="text-xs">{label}</Label>
            <Input type="number" min={0} step={k === "cost_cap_usd" ? "0.1" : "1"} data-testid={`plan-${id}-${k}`} value={l[k] ?? ""} onChange={(e) => setL({ ...l, [k]: e.target.value })} /></div>
        ))}
        <div className="flex flex-col gap-1.5"><Label className="text-xs">Max questions / quiz</Label>
          <Select value={String(l.max_quiz_questions)} onValueChange={(v) => setL({ ...l, max_quiz_questions: Number(v) })}>
            <SelectTrigger data-testid={`plan-${id}-max-questions`}><SelectValue /></SelectTrigger>
            <SelectContent>{[5, 10, 20].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select></div>
      </div>
      <div className="flex flex-wrap items-center gap-2"><Label className="mr-2 text-xs">Difficulty</Label>
        {DIFFICULTIES.map((d) => <Chip key={d} testId={`plan-${id}-diff-${d}`} on={l.difficulties.includes(d)} onClick={() => toggle("difficulties", d)}>{d}</Chip>)}</div>
      <div className="flex flex-wrap items-center gap-2"><Label className="mr-2 text-xs">Subjects</Label>
        <Chip testId={`plan-${id}-subjects-all`} on={l.subject_ids === null} onClick={() => setL({ ...l, subject_ids: l.subject_ids === null ? subjects.slice(0, 5).map((s) => s.id) : null })}>All subjects</Chip>
        {l.subject_ids !== null && subjects.map((s) => <Chip key={s.id} testId={`plan-${id}-subject-${s.id}`} on={l.subject_ids.includes(s.id)} onClick={() => toggle("subject_ids", s.id)}>{s.name}</Chip>)}</div>
      <div className="flex flex-wrap items-center gap-5">
        {FLAGS.map(([k, label]) => <label key={k} className="flex items-center gap-2 text-sm"><Switch data-testid={`plan-${id}-${k}`} checked={l[k]} onCheckedChange={(v) => setL({ ...l, [k]: v })} />{label}</label>)}
        <Select value={l.analytics} onValueChange={(v) => setL({ ...l, analytics: v })}>
          <SelectTrigger data-testid={`plan-${id}-analytics`} className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="basic">Basic scores</SelectItem><SelectItem value="detailed">Detailed tracking</SelectItem><SelectItem value="advanced">Advanced + revision</SelectItem></SelectContent>
        </Select>
      </div>
    </div>
  );
}

export default function PlanEditor({ plan, subjects, subscribers, onSaved }) {
  const [p, setP] = useState({ ...plan, featuresText: plan.features.join("\n") });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const l = p.limits;
      await api.put(`/admin/plans/${plan.id}`, {
        name: p.name, tagline: p.tagline, price_monthly: Number(p.price_monthly), price_yearly: Number(p.price_yearly),
        price_monthly_usd: Number(p.price_monthly_usd), price_yearly_usd: Number(p.price_yearly_usd),
        features: p.featuresText.split("\n"), limits: { ...l, ai_requests: Number(l.ai_requests), quizzes: Number(l.quizzes), cost_cap_usd: Number(l.cost_cap_usd), explanations: l.explanations === "" || l.explanations == null ? null : Number(l.explanations) },
      });
      toast.success(`${p.name} saved`); onSaved();
    } catch (e) { toast.error(formatError(e)); }
    setSaving(false);
  };
  const free = plan.id === "free";
  return (
    <div data-testid={`plan-editor-${plan.id}`} className="grid gap-5 rounded-xl border border-line bg-surface p-6">
      <div className="flex items-center justify-between"><h3 className="font-display text-lg font-bold text-ink">{plan.name}</h3>
        <span data-testid={`plan-subscribers-${plan.id}`} className="rounded-full bg-elevated px-3 py-1 text-xs text-ink-2">{subscribers} learners</span></div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div className="flex flex-col gap-1.5"><Label className="text-xs">Name</Label><Input data-testid={`plan-${plan.id}-name`} value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} /></div>
        <div className="flex flex-col gap-1.5 sm:col-span-1"><Label className="text-xs">Tagline</Label><Input value={p.tagline} onChange={(e) => setP({ ...p, tagline: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {PRICES.map(([k, label]) => <div key={k} className="flex flex-col gap-1.5"><Label className="text-xs">{label}</Label><Input type="number" min={0} disabled={free} data-testid={`plan-${plan.id}-${k.replace(/_/g, "-")}`} value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} /></div>)}
      </div>
      <div className="flex flex-col gap-1.5"><Label className="text-xs">Features shown on the pricing page (one per line)</Label>
        <Textarea rows={4} data-testid={`plan-${plan.id}-features`} value={p.featuresText} onChange={(e) => setP({ ...p, featuresText: e.target.value })} /></div>
      <LimitsFields id={plan.id} l={p.limits} setL={(limits) => setP({ ...p, limits })} subjects={subjects} />
      <Button data-testid={`plan-${plan.id}-save`} disabled={saving} onClick={save} className="justify-self-start rounded-full">Save {plan.name}</Button>
    </div>
  );
}
